import React, { useState, useRef, useCallback } from 'react';
import supabase from '../lib/supabase';
import { Lock, Lightbulb, Palette, Folder, Camera, RefreshCw, Check } from 'lucide-react';
import '../styles/CookieAIReview.css';

const SUPABASE_FUNCTIONS_URL = process.env.REACT_APP_SUPABASE_FUNCTIONS_URL ||
  (process.env.REACT_APP_SUPABASE_URL || '').replace('/rest/v1', '') + '/functions/v1';

const ACCEPTED_IMAGE_TYPES = 'image/jpeg,image/png,image/webp,image/heic,image/heif';

export default function CookieAIColors({ aiModel, existingColors, onAcceptColors }) {
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const [preview, setPreview] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileData, setFileData] = useState(null);   // { base64, mediaType }

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showNotes, setShowNotes] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [error, setError] = useState(null);

  const handleFile = useCallback((file) => {
    if (!file) return;
    if (file.name.toLowerCase().endsWith('.stl')) {
      setError('STL files do not contain color information. Please upload a photo of the cookie.');
      return;
    }
    setFileName(file.name);
    setResult(null);
    setError(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      setPreview(dataUrl);
      setFileData({ base64: dataUrl.split(',')[1], mediaType: file.type || 'image/jpeg' });
    };
    reader.readAsDataURL(file);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleAnalyze = async () => {
    if (!fileData) { setError('Please select a photo first.'); return; }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const authHeader = session?.access_token ? `Bearer ${session.access_token}` : '';

      const response = await fetch(`${SUPABASE_FUNCTIONS_URL}/cookie-ai-colors`, {
        method: 'POST',
        headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image_base64: fileData.base64,
          image_media_type: fileData.mediaType,
          existing_colors: Array.isArray(existingColors) ? existingColors : [],
        }),
      });

      if (response.status === 429) {
        const data = await response.json();
        setError(data.message || 'Daily limit reached. Please try again tomorrow.');
        return;
      }

      const data = await response.json();
      if (data.error && (!data.colors || data.colors.length === 0)) {
        setError(data.ai_notes || data.message || 'Something went wrong. Please try again.');
        return;
      }

      setResult(data);
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = () => {
    if (!result?.colors?.length) return;
    onAcceptColors(result.colors);
  };

  return (
    <div className="cair-container">

      {/* ── Privacy notice ── */}
      <div className="cair-privacy-notice">
        <span>
          <Lock size={15} className="cair-inline-icon" />
          Your image is sent directly to AI for analysis and is <strong>never stored</strong> by us.
        </span>
        {' '}
        <button type="button" className="cair-privacy-toggle" onClick={() => setShowPrivacy(p => !p)}>
          {showPrivacy ? 'Hide details ▲' : 'Find out more ▼'}
        </button>
        {showPrivacy && (
          <div className="cair-privacy-detail">
            <p>
              When you tap "Detect Colors", your image is transmitted securely to our server and forwarded to the AI model for processing. <strong>We do not save your image</strong>. It exists only in memory during the request and is discarded immediately after.
            </p>
            <p>
              We use <strong>{aiModel || 'Claude (Anthropic)'}</strong> for this analysis. Like all AI providers, Anthropic may use submitted data to improve their models in accordance with their{' '}
              <a href="https://www.anthropic.com/privacy" target="_blank" rel="noopener noreferrer">privacy policy</a>.
              We cannot control how the AI provider uses submitted data on their end.
            </p>
            <p>
              For best privacy, use a photo that shows only the cookie and avoids personally identifiable information.
            </p>
          </div>
        )}
      </div>

      {/* ── Tip ── */}
      <div className="cair-tip">
        <Lightbulb size={15} className="cair-inline-icon" />
        For best color detection, place the cookie on a <strong>solid, contrasting background</strong>.
        {existingColors?.length > 0 && (
          <> Your existing colors (<strong>{existingColors.join(', ')}</strong>) will be suggested to the AI for matching.</>
        )}
      </div>

      {/* ── Drop zone ── */}
      <div
        className={`cair-dropzone${preview ? ' cair-dropzone--has-preview' : ''}`}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => fileInputRef.current?.click()}
      >
        {preview ? (
          <img src={preview} alt="Cookie preview" className="cair-preview-img" />
        ) : (
          <div className="cair-dropzone-prompt">
            <span className="cair-dropzone-icon"><Palette size={30} /></span>
            <span>Drop a cookie photo here, or click to browse</span>
          </div>
        )}
      </div>

      {/* Hidden inputs */}
      <input ref={fileInputRef} type="file" accept={ACCEPTED_IMAGE_TYPES} style={{ display: 'none' }}
        onChange={(e) => handleFile(e.target.files?.[0])} />
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
        onChange={(e) => handleFile(e.target.files?.[0])} />

      {/* ── Upload buttons ── */}
      <div className="cair-upload-row">
        <button type="button" className="cair-btn cair-btn--secondary" onClick={() => fileInputRef.current?.click()}>
          <Folder size={18} />Upload Photo
        </button>
        <button type="button" className="cair-btn cair-btn--secondary" onClick={() => cameraInputRef.current?.click()}>
          <Camera size={18} />Take Photo
        </button>
        {fileName && <span className="cair-filename">{fileName}</span>}
      </div>

      {/* ── Analyze button ── */}
      <button
        type="button"
        className="cair-btn cair-btn--primary"
        onClick={handleAnalyze}
        disabled={loading || !fileData}
      >
        {loading
          ? <><RefreshCw size={18} className="cair-icon-spin" />Detecting Colors…</>
          : <><Palette size={18} />Detect Colors</>}
      </button>

      {/* ── Error ── */}
      {error && <div className="cair-error">{error}</div>}

      {/* ── Results ── */}
      {result && result.colors?.length > 0 && (
        <div className="cair-results">
          <div className="cair-ai-badge">AI Detected</div>

          <div className="cair-result-block">
            <div className="cair-colors-header">
              <span className="cair-label">Detected Color Breakdown</span>
              <button type="button" className="cair-btn cair-btn--accept" onClick={handleAccept}>
                <Check size={18} />Apply to Color Plan
              </button>
            </div>
            <div className="cair-color-swatches">
              {result.colors.map((c, i) => (
                <div key={i} className="cair-swatch-row">
                  <span className="cair-swatch" style={{ background: c.hex }} title={c.hex} />
                  <span className="cair-swatch-name">{c.name}</span>
                  <span className="cair-swatch-hex">{c.hex}</span>
                  <span className="cair-swatch-pct">{c.percent}%</span>
                </div>
              ))}
            </div>
            <span className="cair-hint" style={{ marginTop: 4 }}>
              Accepting will replace your current color list and set percentages for all cookies.
            </span>
          </div>

          <button type="button" className="cair-notes-toggle" onClick={() => setShowNotes(n => !n)}>
            {showNotes ? '▲ Hide AI Notes' : '▼ Show AI Notes'}
          </button>
          {showNotes && (
            <div className="cair-notes">{result.ai_notes || 'No additional notes.'}</div>
          )}
        </div>
      )}
    </div>
  );
}
